import React, { ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmModal } from 'slovlex-idsk-react-components';
import { Button, LoaderButton } from 'idsk-react-components';
import routes from '../../../../../../routing/routes';
import { useClient, useClientStatusContext } from '../../../../../../context/ClientStatusContext';
import { useSuccessMessageContext } from '../../../../../../context/SuccessMessageContext';
import { completePracovnyKrok } from '../../../../../../client/pracovnyKrok/pracovnyKrokCompleteClient';
import PracovnyKrokRequest from '../../../../../../model/pracovnyKrok/PracovnyKrokRequest';
import {
  usePracovnyKrokLegislativnyMaterialContext,
} from '../../../../../../context/PracovnyKrokLegislativnyMaterialContext';
import LegislativnyMaterialItem from '../../../../../../model/pracovnyKrok/LegislativnyMaterialItem';
import splitCisloLegMaterialu from '../../../../../../function/splitCisloLegMaterialu';
import useHistoryPageContext from '../../../../../../context/BreadcrumbContext/hook/useHistoryPageContext';

interface Props {
  pracovnyKrok: PracovnyKrokRequest;
  confirmActionQuestion?: string;
  confirmActionDescription?: string;
  reloadDetail?: () => void;
  reloadPracovneKroky?: () => void;
  goToList?: boolean;
  className?: string;
  modalClass?: string;
  legMaterial: LegislativnyMaterialItem;
  redirect?: boolean;
  setIsDropdownOpened?: (isDropdownOpened: boolean) => void,
}

const PracovnyKrokComplete = ({
  pracovnyKrok,
  confirmActionQuestion,
  confirmActionDescription,
  reloadDetail,
  reloadPracovneKroky,
  goToList = false,
  className,
  modalClass,
  legMaterial,
  redirect = true,
  setIsDropdownOpened,
}: Props): ReactElement => {
  const navigate = useNavigate();
  const {
    clearMessages,
    displaySuccess,
  } = useSuccessMessageContext();
  const { clearStatus } = useClientStatusContext();
  const {
    setMaterial,
    setLoadingMaterial,
  } = usePracovnyKrokLegislativnyMaterialContext();

  useEffect(
    () => setMaterial(legMaterial),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [legMaterial?.uuid, legMaterial?.typ,
      legMaterial?.cisloLegislativnehoMaterialu, setMaterial],
  );

  const {
    type,
    year,
    index,
  } = splitCisloLegMaterialu(legMaterial.cisloLegislativnehoMaterialu);

  const {
    wrapClient: completePracovnyKrokClient,
    status: completePracovnyKrokStatus,
  } = useClient(completePracovnyKrok);

  const { historyPages } = useHistoryPageContext();

  function getListRoute(): string {
    if (!historyPages || historyPages.length === 0) {
      return type === 'PI' ? routes.procesy.predbeznaInformacia.list : routes.procesy.list;
    }
    return historyPages[historyPages.length - 1].to;
  }

  useEffect(() => {
    setLoadingMaterial(completePracovnyKrokStatus?.loading);

    if (completePracovnyKrokStatus?.responseCode === 404) {
      navigate(routes.notFound);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completePracovnyKrokStatus?.loading]);

  const onCompletePracovnyKrok = async (): Promise<void> => {
    setMaterial(legMaterial);
    await completePracovnyKrokClient(type, year, index, pracovnyKrok.pracovnyKrokId, pracovnyKrok.pracovnyKrok);
    if (goToList && redirect) {
      navigate(getListRoute());
    }
    doReloadDetail();
    doReloadPracovneKroky();
    displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
  };

  const doReloadDetail = (): void => {
    if (reloadDetail !== undefined) {
      reloadDetail();
    }
  };

  const doReloadPracovneKroky = (): void => {
    if (reloadPracovneKroky !== undefined) {
      reloadPracovneKroky();
    }
  };

  const clearStatusAndMessages = (): void => {
    clearStatus();
    clearMessages();
  };

  const renderButton = (): ReactElement => {
    if (confirmActionQuestion) {
      return (
        <ConfirmModal
          className={modalClass || ''}
          question={confirmActionQuestion}
          description={confirmActionDescription}
          cancelButtonText="Nie"
          confirmButton={
            (
              <Button
                buttonStyle="action"
                size="large"
              >
                Áno
              </Button>
            )
          }
          openButton={(
            <LoaderButton
              className={`pracovny-krok__button ${className || ''}`}
              buttonStyle="link"
              size="small"
              onClick={() => {
                clearStatusAndMessages();
                if (setIsDropdownOpened) {
                  setIsDropdownOpened(false);
                }
              }}
              loading={completePracovnyKrokStatus.loading}
              disableOutsideClickWhileLoading
            >
              {pracovnyKrok.nazov}
            </LoaderButton>
          )}
          doAfterConfirm={onCompletePracovnyKrok}
        />
      );
    }
    return (
      <LoaderButton
        className={`pracovny-krok__button ${className || ''}`}
        buttonStyle="link"
        size="small"
        loading={completePracovnyKrokStatus.loading}
        onClick={onCompletePracovnyKrok}
        disableOutsideClickWhileLoading
      >
        {pracovnyKrok.nazov}
      </LoaderButton>
    );
  };
  return (renderButton());
};

export default PracovnyKrokComplete;
